import React, { useCallback, useState } from 'react';

import Button from '~/components/core/Atomic/Buttons/Button';
import PaymentRequestContainer from '~/components/exposures/PaymentRequestContainer/PaymentRequestContainer';
import AddIcon from '~/components/icons/AddIcon';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import type { Exposure, Finance } from '~/server_shared/swagger/data-contracts';
import { reportAxiosError } from '~/Utils';

import { getIncomeProtectionPaymentSummary } from '../apiService';
import TEXTS from '../texts';
import type { IncomeProtectionDaSettingType, PaymentSummaryType, PrePaymentFormValuesType } from '../types';

import IncomeProtectionPrePaymentDialog from './IncomeProtectionPrePaymentDialog';

const PAYMENT_STAGE = {
  PRE_PAYMENT: 'PRE_PAYMENT',
  PAYMENT: 'PAYMENT',
} as const;

type PaymentStageState =
  | {
      stage: null;
      extra: null;
    }
  | {
      stage: (typeof PAYMENT_STAGE)['PRE_PAYMENT'];
      extra: { paymentsSummary: PaymentSummaryType | null; isLoading: boolean };
    }
  | {
      stage: (typeof PAYMENT_STAGE)['PAYMENT'];
      extra: {
        initialValuesOverrides: PrePaymentFormValuesType & {
          request_extra: { is_income_protection_payment_allowed: true };
        };
      };
    };

const IncomeProtectionAddPayment = ({
  paymentSetting,
  medicalCertificateExpiryDate,
  exposure,
  claim,
  onPaymentAdded,
}: {
  paymentSetting: IncomeProtectionDaSettingType;
  medicalCertificateExpiryDate: string | null;
  exposure: Exposure & { id: number };
  claim: ClaimPolymorph;
  onPaymentAdded: (payment: Finance) => void;
}) => {
  const [paymentStageState, setPaymentStageState] = useState<PaymentStageState>({
    stage: null,
    extra: null,
  });

  const resetPaymentStage = useCallback(() => setPaymentStageState({ stage: null, extra: null }), []);

  const onAddClick = useCallback(async () => {
    setPaymentStageState({
      stage: PAYMENT_STAGE.PRE_PAYMENT,
      extra: { paymentsSummary: null, isLoading: true },
    });

    try {
      const paymentSummaryResponse = await getIncomeProtectionPaymentSummary({
        organizationId: claim.organization_id,
        claimId: claim.id,
        exposureId: exposure.id,
      });
      setPaymentStageState({
        stage: PAYMENT_STAGE.PRE_PAYMENT,
        extra: { paymentsSummary: paymentSummaryResponse.payments_summary, isLoading: false },
      });
    } catch (error) {
      reportAxiosError(error);
      resetPaymentStage();
    }
  }, [claim.id, claim.organization_id, exposure.id, resetPaymentStage]);

  const buttonDisabled = paymentStageState.stage === PAYMENT_STAGE.PRE_PAYMENT && paymentStageState.extra.isLoading;

  return (
    <>
      <Button
        onClick={onAddClick}
        disabled={buttonDisabled}
        variant="contained"
        color="primary"
        startIcon={<AddIcon iconColor="currentColor" />}
      >
        {TEXTS.addPayment}
      </Button>
      {paymentStageState.stage === PAYMENT_STAGE.PRE_PAYMENT ? (
        <IncomeProtectionPrePaymentDialog
          isOpen
          paymentSetting={paymentSetting}
          medicalCertificateExpiryDate={medicalCertificateExpiryDate}
          isLoading={paymentStageState.extra.isLoading}
          paymentsSummary={paymentStageState.extra.paymentsSummary}
          onSubmit={(prePaymentValues) => {
            setPaymentStageState({
              stage: PAYMENT_STAGE.PAYMENT,
              extra: {
                initialValuesOverrides: {
                  ...prePaymentValues,
                  request_extra: { is_income_protection_payment_allowed: true },
                },
              },
            });
          }}
          onCancel={resetPaymentStage}
        />
      ) : paymentStageState.stage === PAYMENT_STAGE.PAYMENT ? (
        <PaymentRequestContainer
          claim={claim}
          onUpdate={onPaymentAdded}
          exposure={exposure}
          payableWithReserve={exposure.indemnity ?? {}}
          payableType="indemnity"
          cardDialogProps={{
            isDialog: true,
            maxWidth: 'md',
            fullWidth: true,
          }}
          onClose={resetPaymentStage}
          overridePaymentRequestStore={[
            {
              type: 'contact_search',
              contact_id: paymentSetting.payee_contact_id,
              contact_full_name: paymentSetting.payee_contact_full_name,
              payee_type: '',
              field_name: 'payee',
              label: 'Payee',
            },
          ]}
          overrideInitialValues={paymentStageState.extra.initialValuesOverrides}
          forceManualDataInit
          enableFutureInServiceDates
        />
      ) : null}
    </>
  );
};

export default IncomeProtectionAddPayment;
